/*
 * <banner-video>
 * ---------------------------------------------------------------------------
 * Reusable, battery-safe autoplay video wrapper for Dawn sections.
 *
 * The element wraps a <template> that holds either a Shopify-hosted <video>
 * or a YouTube/Vimeo <iframe>. The markup inside the <template> is NEVER
 * parsed or downloaded by the browser until we clone it, so on small screens
 * (where we show an image instead) no video bytes are ever fetched.
 *
 * Behaviour:
 *   - Desktop (min-width: 750px): clone the template, autoplay muted+inline,
 *     and pause when scrolled out of view to save CPU/battery.
 *   - Mobile (< 750px): do nothing. The matching image is shown via CSS.
 *   - Resize from mobile -> desktop: inject on the first crossover.
 *   - Resize from desktop -> mobile: pause the playing video.
 */
if (!customElements.get('banner-video')) {
  customElements.define(
    'banner-video',
    class BannerVideo extends HTMLElement {
      constructor() {
        super();
        this.template = this.querySelector('template');
        this.injected = false;
        this.desktopQuery = window.matchMedia('(min-width: 750px)');
        this.onMediaChange = this.evaluate.bind(this);
      }

      connectedCallback() {
        if (!this.template) return;

        // Listen for breakpoint changes (addEventListener with Safari fallback).
        if (this.desktopQuery.addEventListener) {
          this.desktopQuery.addEventListener('change', this.onMediaChange);
        } else if (this.desktopQuery.addListener) {
          this.desktopQuery.addListener(this.onMediaChange);
        }

        this.evaluate();
      }

      disconnectedCallback() {
        if (this.desktopQuery.removeEventListener) {
          this.desktopQuery.removeEventListener('change', this.onMediaChange);
        } else if (this.desktopQuery.removeListener) {
          this.desktopQuery.removeListener(this.onMediaChange);
        }
        if (this.observer) this.observer.disconnect();
      }

      evaluate() {
        if (this.desktopQuery.matches) {
          this.inject();
          this.play();
        } else {
          this.pause();
        }
      }

      inject() {
        if (this.injected) return;
        this.injected = true;
        this.appendChild(this.template.content.cloneNode(true));

        const video = this.querySelector('video');
        if (video) {
          // Muted is required for programmatic autoplay in every browser.
          video.muted = true;
          this.observeVisibility(video);
        }
      }

      play() {
        const video = this.querySelector('video');
        if (!video) return; // iframes (YouTube/Vimeo) autoplay via their own URL params
        const result = video.play();
        if (result && typeof result.catch === 'function') result.catch(() => {});
      }

      pause() {
        const video = this.querySelector('video');
        if (video) video.pause();
      }

      observeVisibility(video) {
        if (!('IntersectionObserver' in window)) return;
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!this.desktopQuery.matches) return;
              if (entry.isIntersecting) {
                const result = video.play();
                if (result && typeof result.catch === 'function') result.catch(() => {});
              } else {
                video.pause();
              }
            });
          },
          { threshold: 0 }
        );
        this.observer.observe(video);
      }
    }
  );
}